import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { EditImageIcon } from "../icons";

const avatarVariants = cva(
  "relative flex items-end justify-end overflow-hidden rounded-[0.3125rem] border border-solid border-gray-100 box-border transition-all",
  {
    variants: {
      size: {
        default: "size-full",
        sm: "size-12",
        md: "size-16",
        lg: "size-20",
        xl: "size-24",
        "2xl": "size-32",
      },
    },
    defaultVariants: {
      size: "default",
    },
  }
);

export interface AvatarProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof avatarVariants> {
  src?: string;
  alt?: string;
  fallback?: React.ReactNode;
  /**
   * Makes the avatar editable with a small edit icon in the bottom-right corner
   */
  editable?: boolean;
  /**
   * Callback when the avatar is clicked (typically used with editable variant)
   */
  onEditClick?: () => void;
}

/**
 * Avatar component for displaying profile pictures or user images
 *
 * Based on Figma design with rounded square shape and border.
 * Supports image source with automatic fallback to background color.
 * Can be made editable with a small edit icon in the bottom-right corner.
 *
 * @example
 * ```tsx
 * <Avatar src="/path/to/image.jpg" alt="User profile" />
 * <Avatar size="md" fallback={<span>JD</span>} />
 * <Avatar src="/path/to/image.jpg" editable onEditClick={() => console.log('Edit clicked')} />
 * ```
 */
const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, size, src, alt = "", fallback, editable = false, onEditClick, ...props }, ref) => {
    const handleClick = () => {
      if (editable && onEditClick) {
        onEditClick();
      }
    };

    return (
      <div
        ref={ref}
        className={cn(
          avatarVariants({ size, className }),
          editable && "cursor-pointer"
        )}
        onClick={handleClick}
        {...props}
      >
        {/* Background layer */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none rounded-[0.3125rem]"
        >
          {/* Fallback background */}
          <div className="absolute bg-gray-50 inset-0 rounded-[0.3125rem]" />

          {/* Image layer */}
          {src && (
            <img
              alt={alt}
              className="absolute max-w-none object-center object-cover rounded-[0.3125rem] size-full"
              src={src}
            />
          )}

          {/* Fallback content (initials, icon, etc.) */}
          {!src && fallback && (
            <div className="absolute inset-0 flex items-center justify-center text-gray-700 font-montserrat font-semibold rounded-[0.3125rem]">
              {fallback}
            </div>
          )}
        </div>

        {/* Edit icon badge (when editable) */}
        {editable && (
          <div className="bg-white border border-gray-100 border-solid box-border flex items-center justify-center p-[8px] relative z-10 rounded-[0.3125rem] size-[1rem]">
            <EditImageIcon size={8} className="text-gray-500" />
          </div>
        )}

        {/* Padding for proper border display (when not editable) */}
        {!editable && (
          <div className="p-[8px] relative z-10 pointer-events-none" />
        )}
      </div>
    );
  }
);

Avatar.displayName = "Avatar";

export { Avatar, avatarVariants };
